// Message Templates Page — List, Create, Edit, Toggle, Delete

import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import MainLayout from '../components/layout/MainLayout.jsx';
import { Button, Alert } from '../components/common/index.js';
import {
  fetchTemplates,
  createTemplate,
  updateTemplate,
  toggleTemplateActive,
  deleteTemplate,
  clearError,
} from '../redux/slices/templatesSlice';
import {
  FiPlus,
  FiSearch,
  FiEdit2,
  FiTrash2,
  FiX,
  FiType,
  FiAlertTriangle,
  FiFileText,
  FiCheckCircle,
  FiEyeOff,
  FiRefreshCw,
  FiCopy,
} from 'react-icons/fi';

// ─── Avatar colour pool ───────────────────────────────────────────────────────
const GRADIENTS = [
  'from-violet-500 to-indigo-600',
  'from-sky-500 to-cyan-600',
  'from-emerald-500 to-teal-600',
  'from-rose-500 to-pink-600',
  'from-amber-500 to-orange-500',
  'from-fuchsia-500 to-purple-600',
];
const grad = (i) => GRADIENTS[i % GRADIENTS.length];

// ─── Active Toggle ────────────────────────────────────────────────────────────
const ActiveToggle = ({ active, onChange }) => (
  <button
    type="button"
    onClick={onChange}
    className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-violet-400 focus:ring-offset-1 ${
      active ? 'bg-emerald-500' : 'bg-slate-300'
    }`}
    role="switch"
    aria-checked={active}
  >
    <span className={`pointer-events-none inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow-sm transition-transform duration-200 ${active ? 'translate-x-4' : 'translate-x-0'}`} />
  </button>
);

// ─── Modal Shell ──────────────────────────────────────────────────────────────
const Modal = ({ title, onClose, children }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center">
    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} />
    <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 animate-fade-in-up max-h-[90vh] overflow-y-auto">
      <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100 sticky top-0 bg-white rounded-t-2xl z-10">
        <h3 className="text-base font-bold text-slate-900">{title}</h3>
        <button
          onClick={onClose}
          className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
        >
          <FiX className="w-4 h-4" />
        </button>
      </div>
      <div className="px-6 py-5">{children}</div>
    </div>
  </div>
);

// ─── Template Form (Add / Edit) ───────────────────────────────────────────────
const EMPTY_FORM = { title: '', message: '' };

const TemplateForm = ({ initial = EMPTY_FORM, isEdit = false, submitting, apiError, onSubmit, onClose }) => {
  const [form, setForm]     = useState(initial);
  const [errors, setErrors] = useState({});

  const set = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.title.trim())   e.title = 'Title is required';
    if (!form.message.trim()) e.message = 'Message is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    onSubmit({ title: form.title.trim(), message: form.message });
  };

  const apiErrorMsg = apiError
    ? typeof apiError === 'string'
      ? apiError
      : Object.entries(apiError).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join(' | ')
    : null;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {apiErrorMsg && <Alert type="error" message={apiErrorMsg} />}

      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-1.5">Title *</label>
        <div className="relative">
          <FiType className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4 pointer-events-none" />
          <input
            type="text"
            placeholder="e.g. Follow Up"
            value={form.title}
            onChange={set('title')}
            disabled={submitting}
            className={`w-full pl-10 pr-4 py-2.5 border rounded-xl text-sm text-slate-800 placeholder-slate-400 transition-all focus:outline-none focus:ring-2 ${
              errors.title
                ? 'border-rose-400 bg-rose-50 focus:ring-rose-300'
                : 'border-slate-200 bg-white focus:ring-violet-300 focus:border-violet-400'
            }`}
          />
        </div>
        {errors.title && <p className="text-rose-500 text-xs mt-1 font-medium">{errors.title}</p>}
      </div>

      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-1.5">Message *</label>
        <textarea
          rows={6}
          placeholder="Enter the message customers will receive..."
          value={form.message}
          onChange={set('message')}
          disabled={submitting}
          className={`w-full px-4 py-2.5 border rounded-xl text-sm text-slate-800 placeholder-slate-400 transition-all focus:outline-none focus:ring-2 resize-none leading-relaxed ${
            errors.message
              ? 'border-rose-400 bg-rose-50 focus:ring-rose-300'
              : 'border-slate-200 bg-white focus:ring-violet-300 focus:border-violet-400'
          }`}
        />
        <div className="flex items-center justify-between mt-1">
          {errors.message
            ? <p className="text-rose-500 text-xs font-medium">{errors.message}</p>
            : <span />}
          <p className="text-xs text-slate-400">{form.message.length} characters</p>
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="secondary" size="md" onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button type="submit" variant="primary" size="md" loading={submitting}>
          {isEdit ? 'Save Changes' : 'Create Template'}
        </Button>
      </div>
    </form>
  );
};

// ─── Delete Confirm Modal ─────────────────────────────────────────────────────
const DeleteConfirm = ({ template, submitting, apiError, onConfirm, onClose }) => {
  const errMsg = apiError ? (typeof apiError === 'string' ? apiError : 'Delete failed. Please try again.') : null;

  return (
    <Modal title="Delete Template" onClose={onClose}>
      <div className="flex flex-col items-center text-center gap-4">
        <div className="w-14 h-14 rounded-full bg-rose-100 flex items-center justify-center">
          <FiAlertTriangle className="w-7 h-7 text-rose-500" />
        </div>
        <div>
          <p className="font-semibold text-slate-800 mb-1">
            Delete <span className="text-rose-600">{template.title}</span>?
          </p>
          <p className="text-slate-500 text-sm">
            This permanently removes the template. To hide it from sales without deleting, turn it inactive instead.
          </p>
        </div>
        {errMsg && <Alert type="error" message={errMsg} className="w-full text-left" />}
        <div className="flex gap-3 w-full">
          <Button variant="secondary" size="md" onClick={onClose} disabled={submitting} className="flex-1">Cancel</Button>
          <Button variant="danger" size="md" onClick={onConfirm} loading={submitting} className="flex-1">Yes, Delete</Button>
        </div>
      </div>
    </Modal>
  );
};

// ─── Stat Chip ────────────────────────────────────────────────────────────────
const StatChip = ({ icon: Icon, iconBg, iconColor, label, value, valueColor }) => (
  <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-xl px-4 py-3 shadow-sm flex-1 min-w-[90px]">
    <div className={`w-8 h-8 rounded-lg ${iconBg} flex items-center justify-center shrink-0`}>
      <Icon className={`w-4 h-4 ${iconColor}`} />
    </div>
    <div className="min-w-0">
      <p className="text-xs text-slate-400 leading-none mb-0.5">{label}</p>
      <p className={`text-lg font-bold leading-none ${valueColor}`}>{value}</p>
    </div>
  </div>
);

// ─── Spinner / Empty State ────────────────────────────────────────────────────
const Spinner = () => (
  <div className="flex items-center justify-center py-20">
    <svg className="animate-spin w-8 h-8 text-violet-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  </div>
);

const EmptyState = ({ search }) => (
  <div className="flex flex-col items-center justify-center py-20 text-center px-4">
    <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4 bg-violet-50">
      <FiFileText className="w-7 h-7 text-violet-400" />
    </div>
    <p className="text-slate-700 font-semibold mb-1">
      {search ? 'No results found' : 'No templates yet'}
    </p>
    <p className="text-slate-400 text-sm">
      {search ? 'Try a different search term.' : 'Add your first message template to get started.'}
    </p>
  </div>
);

// ─── Template Card (mobile/tablet) ────────────────────────────────────────────
const TemplateCard = ({ t, index, onEdit, onDelete, onToggle, onCopy, copied }) => (
  <div className={`bg-white rounded-2xl border shadow-sm overflow-hidden animate-fade-in-up ${t.is_active ? 'border-slate-200' : 'border-slate-200 opacity-75'}`}>
    <div className={`h-1 w-full bg-gradient-to-r ${t.is_active ? grad(index) : 'from-slate-300 to-slate-400'}`} />
    <div className="p-4">
      <div className="flex items-start gap-3">
        <div className={`w-11 h-11 rounded-xl bg-gradient-to-br flex items-center justify-center text-white shrink-0 shadow-sm ${t.is_active ? grad(index) : 'from-slate-300 to-slate-400'}`}>
          <FiFileText className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-bold text-slate-900 truncate flex-1">{t.title}</p>
            {!t.is_active && (
              <span className="text-[10px] font-bold text-slate-500 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded-full shrink-0">Hidden</span>
            )}
          </div>
          <p className="text-xs text-slate-500 mt-1 line-clamp-3 whitespace-pre-wrap leading-relaxed">{t.message}</p>
        </div>
      </div>
      <div className="border-t border-slate-100 mt-3 pt-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ActiveToggle active={t.is_active} onChange={onToggle} />
          <span className={`text-xs font-semibold ${t.is_active ? 'text-emerald-600' : 'text-slate-400'}`}>
            {t.is_active ? 'Active' : 'Inactive'}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={onCopy} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 transition-all" title="Copy message">
            {copied ? <FiCheckCircle className="w-3.5 h-3.5 text-emerald-500" /> : <FiCopy className="w-3.5 h-3.5" />}
          </button>
          <button onClick={onEdit} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-violet-600 hover:bg-violet-50 transition-all" title="Edit">
            <FiEdit2 className="w-3.5 h-3.5" />
          </button>
          <button onClick={onDelete} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-all" title="Delete">
            <FiTrash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  </div>
);

// ─── Main Page ────────────────────────────────────────────────────────────────
const TABS = [
  { id: 'all',      label: 'All' },
  { id: 'active',   label: 'Active' },
  { id: 'inactive', label: 'Inactive' },
];

const TemplatesPage = () => {
  const dispatch = useDispatch();
  const { list, count, loading, submitting, error } = useSelector((s) => s.templates);

  const [search,       setSearch]       = useState('');
  const [activeTab,    setActiveTab]    = useState('all');
  const [showAdd,      setShowAdd]      = useState(false);
  const [editTarget,   setEditTarget]   = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [copiedId,     setCopiedId]     = useState(null);

  useEffect(() => {
    dispatch(fetchTemplates());
  }, [dispatch]);

  const closeAdd  = useCallback(() => { setShowAdd(false);     dispatch(clearError()); }, [dispatch]);
  const closeEdit = useCallback(() => { setEditTarget(null);   dispatch(clearError()); }, [dispatch]);
  const closeDel  = useCallback(() => { setDeleteTarget(null); dispatch(clearError()); }, [dispatch]);

  const handleCreate = async (data) => {
    const res = await dispatch(createTemplate(data));
    if (!res.error) closeAdd();
  };

  const handleUpdate = async (data) => {
    const res = await dispatch(updateTemplate({ id: editTarget.id, data }));
    if (!res.error) closeEdit();
  };

  const handleDelete = async () => {
    const res = await dispatch(deleteTemplate(deleteTarget.id));
    if (!res.error) closeDel();
  };

  const handleToggleActive = (t) => dispatch(toggleTemplateActive({ id: t.id, is_active: !t.is_active }));
  const handleRefresh = () => dispatch(fetchTemplates());

  const handleCopy = (t) => {
    navigator.clipboard?.writeText(t.message);
    setCopiedId(t.id);
    setTimeout(() => setCopiedId((id) => (id === t.id ? null : id)), 1500);
  };

  // ── Computed lists per tab ──────────────────────────────────────────────────
  const tabFiltered = (() => {
    switch (activeTab) {
      case 'active':   return list.filter((t) => t.is_active);
      case 'inactive': return list.filter((t) => !t.is_active);
      default:         return list;
    }
  })();

  const q = search.toLowerCase();
  const filtered = tabFiltered.filter((t) =>
    (t.title || '').toLowerCase().includes(q) ||
    (t.message || '').toLowerCase().includes(q)
  );

  const activeCount   = list.filter((t) => t.is_active).length;
  const inactiveCount = list.filter((t) => !t.is_active).length;

  const tabCounts = { all: count, active: activeCount, inactive: inactiveCount };

  return (
    <MainLayout>
      <div className="space-y-5">

        {/* ── Page header ─────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between gap-3 animate-fade-in-up">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Templates</h1>
            <p className="text-slate-500 text-sm mt-0.5">{count} message {count === 1 ? 'template' : 'templates'}</p>
          </div>
          <button
            onClick={() => setShowAdd(true)}
            className="flex items-center gap-2 px-3 sm:px-4 py-2.5 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white text-sm font-semibold rounded-xl shadow-md shadow-violet-500/20 transition-all active:scale-95 shrink-0"
          >
            <FiPlus className="w-4 h-4" />
            <span className="hidden sm:inline">Add Template</span>
            <span className="sm:hidden">Add</span>
          </button>
        </div>

        {/* ── Stats strip ─────────────────────────────────────────────────── */}
        <div className="flex gap-3 animate-fade-in-up" style={{ animationDelay: '0.05s' }}>
          <StatChip icon={FiFileText}   iconBg="bg-violet-50"  iconColor="text-violet-500"  label="Total"    value={count}         valueColor="text-slate-800" />
          <StatChip icon={FiCheckCircle} iconBg="bg-emerald-50" iconColor="text-emerald-500" label="Active"   value={activeCount}   valueColor="text-emerald-600" />
          <StatChip icon={FiEyeOff}     iconBg="bg-slate-100"  iconColor="text-slate-400"   label="Inactive" value={inactiveCount} valueColor="text-slate-500" />
        </div>

        {/* ── Tabs ────────────────────────────────────────────────────────── */}
        <div className="flex gap-1 bg-slate-100 p-1 rounded-xl w-fit animate-fade-in-up" style={{ animationDelay: '0.07s' }}>
          {TABS.map((tab) => {
            const isActive = activeTab === tab.id;
            const cnt = tabCounts[tab.id];
            return (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setSearch(''); }}
                className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold transition-all ${
                  isActive ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {tab.label}
                {cnt !== null && cnt !== undefined && (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${
                    isActive ? 'bg-violet-100 text-violet-700' : 'bg-slate-200 text-slate-500'
                  }`}>
                    {cnt}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* ── Search + Refresh ────────────────────────────────────────────── */}
        <div className="flex gap-2 animate-fade-in-up" style={{ animationDelay: '0.09s' }}>
          <div className="relative flex-1">
            <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search templates by title or message..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-11 pr-10 py-3 border border-slate-200 rounded-xl bg-white text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-300 focus:border-violet-400 transition-all shadow-sm"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all"
              >
                <FiX className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <button
            onClick={handleRefresh}
            disabled={loading}
            title="Refresh"
            className="w-12 h-12 shrink-0 flex items-center justify-center bg-white border border-slate-200 rounded-xl text-slate-500 hover:text-violet-600 hover:border-violet-300 hover:bg-violet-50 shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FiRefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {error && !showAdd && !editTarget && !deleteTarget && (
          <Alert type="error" message={typeof error === 'string' ? error : 'Something went wrong.'} />
        )}

        {/* ── Mobile / Tablet: card grid ───────────────────────────────────── */}
        <div className="lg:hidden animate-fade-in-up" style={{ animationDelay: '0.11s' }}>
          {loading ? (
            <Spinner />
          ) : filtered.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm">
              <EmptyState search={search} />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {filtered.map((t, i) => (
                <TemplateCard
                  key={t.id}
                  t={t}
                  index={i}
                  copied={copiedId === t.id}
                  onCopy={() => handleCopy(t)}
                  onEdit={() => setEditTarget(t)}
                  onDelete={() => setDeleteTarget(t)}
                  onToggle={() => handleToggleActive(t)}
                />
              ))}
            </div>
          )}
        </div>

        {/* ── Desktop: table ───────────────────────────────────────────────── */}
        <div className="hidden lg:block bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden animate-fade-in-up" style={{ animationDelay: '0.11s' }}>
          {loading ? (
            <Spinner />
          ) : filtered.length === 0 ? (
            <EmptyState search={search} />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100">
                    <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-6 py-3.5 w-12">#</th>
                    <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-6 py-3.5">Title</th>
                    <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-6 py-3.5">Message</th>
                    <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-6 py-3.5">Status</th>
                    <th className="text-right text-xs font-semibold text-slate-400 uppercase tracking-wider px-6 py-3.5">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {filtered.map((t, index) => (
                    <tr key={t.id} className={`hover:bg-slate-50/60 transition-colors ${!t.is_active ? 'opacity-70' : ''}`}>
                      <td className="px-6 py-4 text-sm font-semibold text-slate-300">{index + 1}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-lg bg-gradient-to-br flex items-center justify-center text-white shrink-0 ${t.is_active ? grad(index) : 'from-slate-300 to-slate-400'}`}>
                            <FiFileText className="w-4 h-4" />
                          </div>
                          <span className="text-sm font-semibold text-slate-800 whitespace-nowrap">{t.title}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-slate-500 line-clamp-2 max-w-md whitespace-pre-wrap leading-relaxed">{t.message}</p>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <ActiveToggle active={t.is_active} onChange={() => handleToggleActive(t)} />
                          <span className={`text-xs font-semibold ${t.is_active ? 'text-emerald-600' : 'text-slate-400'}`}>
                            {t.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button onClick={() => handleCopy(t)} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 transition-all" title="Copy message">
                            {copiedId === t.id ? <FiCheckCircle className="w-3.5 h-3.5 text-emerald-500" /> : <FiCopy className="w-3.5 h-3.5" />}
                          </button>
                          <button onClick={() => setEditTarget(t)} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-violet-600 hover:bg-violet-50 transition-all" title="Edit">
                            <FiEdit2 className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => setDeleteTarget(t)} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-all" title="Delete">
                            <FiTrash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>

      {/* ── Modals ─────────────────────────────────────────────────────────── */}
      {showAdd && (
        <Modal title="Add New Template" onClose={closeAdd}>
          <TemplateForm submitting={submitting} apiError={error} onSubmit={handleCreate} onClose={closeAdd} />
        </Modal>
      )}

      {editTarget && (
        <Modal title="Edit Template" onClose={closeEdit}>
          <TemplateForm
            isEdit
            initial={{ title: editTarget.title || '', message: editTarget.message || '' }}
            submitting={submitting}
            apiError={error}
            onSubmit={handleUpdate}
            onClose={closeEdit}
          />
        </Modal>
      )}

      {deleteTarget && (
        <DeleteConfirm
          template={deleteTarget}
          submitting={submitting}
          apiError={error}
          onConfirm={handleDelete}
          onClose={closeDel}
        />
      )}
    </MainLayout>
  );
};

export default TemplatesPage;
